import { useParams, Link } from 'react-router';
import { Button } from '@lms/ui';
import {
  ArrowLeft,
  FileText,
  CheckCircle2,
  Loader2,
  CheckCircle,
  MessageSquare,
  Send,
  Sparkles,
  AlertCircle,
  ArrowRight,
  Lock,
  Music,
  ExternalLink,
  Video,
  Globe,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMaterial, useCompleteMaterial } from '../hooks/useMaterials';
import { useEnrollments } from '../hooks/useEnrollments';
import { useCreateReflection } from '../hooks/useRegistrations';
import { useAuthStore } from '../store/auth';
import { MediaPlayer } from '../components/MediaPlayer';
import type { Enrollment, LearningFlow, MaterialMedia, Program } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { apiBaseUrl } from '../lib/api-client';
import { CurriculumPath, type CurriculumPathItem } from '../components/CurriculumPath';

function getReadableContext(value?: string | null): string {
  return (value || '')
    .split(/\r?\n/)
    .map(line =>
      line
        .replace(/(?:https?:\/{1,2}|www\.)[^\s<>]+/gi, '')
        .replace(/\b(?:youtu\.be|drive\.google\.com|youtube\.com)\/\S+/gi, '')
        .trim()
    )
    .filter(
      line =>
        line &&
        !/^(?:[\w.-]+\/)+[\w.?%#=&-]+$/.test(line) &&
        !/\.(?:mp4|webm|mov|mp3|m4a|ogg|wav|pdf)(?:[?#].*)?$/i.test(line)
    )
    .join('\n');
}

export function MaterialDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: material, isLoading: materialLoading, error } = useMaterial(id || '');
  const { data: enrollments, isLoading: enrollmentsLoading } = useEnrollments({ userId: user?.id });
  const {
    mutate: complete,
    isPending: completePending,
    isError: completeFailed,
  } = useCompleteMaterial();
  const {
    mutate: submitReflection,
    isPending: submittingReflection,
    isError: reflectionFailed,
  } = useCreateReflection();
  const { t } = useLanguage();

  const [reflectionText, setReflectionText] = useState('');
  const [showReflectionForm, setShowReflectionForm] = useState(false);

  useEffect(() => {
    if (!showReflectionForm) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('material-reflection')?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [showReflectionForm]);

  // Find which program this material belongs to across 6-Tier curriculum or legacy flows
  const currentEnrollment = enrollments?.find((e: Enrollment) => {
    const inStages = e.program?.levels?.some(l =>
      l.stages?.some(s =>
        s.subjects?.some(sub => sub.sessions?.some(ses => ses.materials?.some(m => m.id === id)))
      )
    );
    if (inStages) return true;

    const inLevelFlows = e.program?.levels?.some(l =>
      l.flows?.some(f => f.materials?.some(m => m.id === id))
    );
    const inProgramFlows = (e.program as Program & { flows?: LearningFlow[] }).flows?.some(flow =>
      flow.materials?.some(m => m.id === id)
    );
    return !!inLevelFlows || !!inProgramFlows;
  });

  const program = currentEnrollment?.program;

  // Find current session, subject, stage, level context
  const getSessionHierarchy = () => {
    if (!program?.levels) return null;
    for (const lvl of program.levels) {
      for (const stg of lvl.stages || []) {
        for (const sbj of stg.subjects || []) {
          const sessions = sbj.sessions || [];
          const learningSequence = sessions.flatMap(ses =>
            (ses.materials || []).map(item => ({ item, session: ses }))
          );
          const materialIndex = learningSequence.findIndex(entry => entry.item.id === id);
          if (materialIndex >= 0) {
            const current = learningSequence[materialIndex];
            const previous = learningSequence[materialIndex - 1];
            const following = learningSequence[materialIndex + 1];
            return {
              level: lvl,
              stage: stg,
              subject: sbj,
              session: current.session,
              previousMaterial: previous?.item ?? null,
              followingMaterial: following?.item ?? null,
              previousMaterialSession: previous?.session ?? null,
              followingMaterialSession: following?.session ?? null,
            };
          }
        }
      }
    }
    return null;
  };

  const sessionHierarchy = getSessionHierarchy();
  const legacyFlow = [
    ...(program?.levels?.flatMap(level => level.flows || []) || []),
    ...((program as (Program & { flows?: LearningFlow[] }) | undefined)?.flows || []),
  ].find(flow => flow.materials?.some(item => item.id === id));
  const legacyMaterials = [...(legacyFlow?.materials || [])].sort((a, b) => a.order - b.order);
  const legacyMaterialIndex = legacyMaterials.findIndex(item => item.id === id);
  const curriculumReturnUrl =
    program && sessionHierarchy
      ? `/programs/${program.id}#session-${sessionHierarchy.session.id}`
      : program
        ? `/programs/${program.id}`
        : '/programs';

  if (materialLoading || enrollmentsLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-sm text-on-surface-variant font-body">
          {t(copy.materialDetail.loading)}
        </p>
      </div>
    );
  }

  const isPreviousMaterialNotCompleted =
    error instanceof Error &&
    (error.message.includes('PREVIOUS') ||
      error.message.includes('PREREQUISITE') ||
      error.message.includes('QUOTA'));

  if (error || isPreviousMaterialNotCompleted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-surface-container-low rounded-3xl">
        <div className="size-20 rounded-full bg-primary/5 flex items-center justify-center mb-6">
          <Lock size={40} className="text-primary/20" />
        </div>
        <h2 className="text-2xl font-headline font-bold text-on-surface mb-2">
          {t(copy.materialDetail.lockedTitle)}
        </h2>
        <p className="text-on-surface-variant max-w-md mx-auto mb-8 font-body leading-relaxed">
          {error instanceof Error ? error.message : t(copy.materialDetail.lockedDesc)}
        </p>
        <Link
          to={curriculumReturnUrl}
          className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-on-primary no-underline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          {t(copy.materialDetail.backToCurriculum)}
        </Link>
      </div>
    );
  }

  if (!material) {
    return (
      <div className="text-center py-12">
        <p className="text-on-surface-variant">{t(copy.materialDetail.notFound)}</p>
      </div>
    );
  }

  const isCompleted = currentEnrollment?.completions?.some(c => c.materialId === material.id);
  const previousMaterial =
    sessionHierarchy?.previousMaterial || legacyMaterials[legacyMaterialIndex - 1];
  const followingMaterial =
    sessionHierarchy?.followingMaterial || legacyMaterials[legacyMaterialIndex + 1];
  const contextText =
    getReadableContext(material.content) || getReadableContext(material.description);
  const materialTypeLabel = t(
    copy.materials.types[material.type.toLowerCase() as keyof typeof copy.materials.types]
  );
  const curriculumPathItems: CurriculumPathItem[] = sessionHierarchy
    ? [
        {
          key: 'program',
          label: t(copy.curriculum.program),
          value: program?.name || t(copy.curriculum.program),
          href: program ? `/programs/${program.id}` : '/programs',
        },
        {
          key: 'level',
          label: t(copy.curriculum.level),
          value: sessionHierarchy.level.name,
          href: program ? `/programs/${program.id}#level-${sessionHierarchy.level.id}` : undefined,
        },
        {
          key: 'stage',
          label: t(copy.curriculum.stage),
          value: sessionHierarchy.stage.name,
          href: program ? `/programs/${program.id}#stage-${sessionHierarchy.stage.id}` : undefined,
        },
        {
          key: 'subject',
          label: t(copy.curriculum.subject),
          value: sessionHierarchy.subject.name,
          href: program
            ? `/programs/${program.id}#subject-${sessionHierarchy.subject.id}`
            : undefined,
        },
        {
          key: 'session',
          label: t(copy.curriculum.session),
          value: `#${sessionHierarchy.session.sessionNumber} ${sessionHierarchy.session.title}`,
          href: curriculumReturnUrl,
        },
        {
          key: 'material',
          label: t(copy.curriculum.material),
          value: material.title,
          current: true,
        },
      ]
    : [
        {
          key: 'program',
          label: t(copy.curriculum.program),
          value: program?.name || t(copy.curriculum.program),
          href: program ? `/programs/${program.id}` : '/programs',
        },
        {
          key: 'material',
          label: t(copy.curriculum.material),
          value: material.title,
          current: true,
        },
      ];

  const handleComplete = () => {
    if (currentEnrollment && id) {
      void complete(
        { id, enrollmentId: currentEnrollment.id },
        {
          onSuccess: () => {
            setShowReflectionForm(true);
          },
        }
      );
    }
  };

  const handleReflectionSubmit = () => {
    if (!user || !reflectionText.trim()) return;
    void submitReflection(
      {
        materialId: id,
        userId: user.id,
        prompt: t(copy.materialDetail.reflectionPrompt),
        response: reflectionText,
        programId: program?.id,
      },
      {
        onSuccess: () => {
          setReflectionText('');
          setShowReflectionForm(false);
        },
      }
    );
  };

  return (
    <div className="space-y-7 animate-fadeIn pb-12 sm:space-y-9">
      {/* Breadcrumb & Back Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={curriculumReturnUrl}
          className="inline-flex items-center gap-2 rounded-xl bg-surface-container px-3 py-2 font-body text-on-surface-variant no-underline transition-colors hover:bg-surface-container-high hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
        >
          <ArrowLeft size={16} />
          <span className="text-xs font-bold uppercase tracking-wider">
            {t(copy.materialDetail.backToCurriculum)}
          </span>
        </Link>
      </div>

      <CurriculumPath
        ariaLabel={t(copy.curriculum.ariaLabel)}
        eyebrow={t(copy.curriculum.currentPosition)}
        items={curriculumPathItems}
      />

      {/* Header Area */}
      <div className="flex flex-col gap-4 rounded-3xl bg-surface-container-low p-5 sm:p-7 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-label font-bold text-primary bg-primary/5 px-2 py-0.5 rounded tracking-widest uppercase">
              {materialTypeLabel}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-headline font-bold text-on-surface tracking-tight leading-tight">
            {material.title}
          </h1>
        </div>
        {isCompleted && (
          <div className="flex items-center gap-2 self-start rounded-xl bg-success-container px-4 py-2 text-on-success-container animate-fadeIn shrink-0">
            <CheckCircle size={18} />
            <span className="text-sm font-bold font-headline uppercase tracking-wide">
              {t(copy.materialDetail.learned)}
            </span>
          </div>
        )}
      </div>

      <nav
        aria-label={t(copy.materialDetail.learningNavigation)}
        className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]"
      >
        {[
          { href: '#material-media', label: copy.materialDetail.jumpToContent },
          ...(contextText
            ? [{ href: '#material-context', label: copy.materialDetail.jumpToSummary }]
            : []),
          ...(material.media?.length
            ? [{ href: '#material-attachments', label: copy.materialDetail.jumpToAttachments }]
            : []),
          ...(material.evaluations?.length
            ? [{ href: '#material-assessments', label: copy.materialDetail.jumpToAssessments }]
            : []),
          ...(showReflectionForm || isCompleted
            ? [{ href: '#material-reflection', label: copy.materialDetail.jumpToReflection }]
            : []),
        ].map(item => (
          <a
            key={item.href}
            href={item.href}
            className="inline-flex min-h-11 shrink-0 items-center rounded-lg bg-surface-container-low px-4 py-2 text-xs font-bold text-primary no-underline transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
          >
            {t(item.label)}
          </a>
        ))}
      </nav>

      {/* Live Tatap Muka (Google Meet / Zoom) Banner */}
      {(material.meetingUrl || sessionHierarchy?.session?.meetingUrl) && (
        <div className="bg-surface-container-low p-5 md:p-6 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fadeIn">
          <div className="flex items-center gap-3.5">
            <div className="size-12 rounded-2xl bg-primary text-on-primary flex items-center justify-center shadow-level1 shrink-0">
              <Globe size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-success" aria-hidden="true" />
                  {t(copy.materialDetail.liveBadge)}
                </span>
              </div>
              <h3 className="font-headline font-bold text-on-surface text-base md:text-lg mt-0.5">
                {t(copy.materialDetail.liveTitle)}
              </h3>
              <p className="text-xs text-on-surface-variant/80">
                {t(copy.materialDetail.liveDesc)}
              </p>
            </div>
          </div>

          <a
            href={material.meetingUrl || sessionHierarchy?.session?.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-primary to-primary-container px-6 py-3 text-sm font-bold text-on-primary no-underline transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
          >
            <span>{t(copy.materialDetail.liveJoin)}</span>
            <ExternalLink size={15} />
          </a>
        </div>
      )}

      <div className="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-10">
        {/* Main Content Column */}
        <div className="min-w-0 space-y-8">
          {/* Media Player Glass Card */}
          <div
            id="material-media"
            className="scroll-mt-24 overflow-hidden rounded-3xl bg-surface-container-lowest shadow-level1"
          >
            <MediaPlayer key={material.id} material={material} />

            {/* Completion Bar - Subtle Progress Tracking */}
            {!isCompleted && (
              <div className="flex flex-col gap-4 bg-surface-container-low p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-xl text-primary">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-on-surface uppercase tracking-tight">
                      {t(copy.materialDetail.milestoneReady)}
                    </p>
                    <p className="text-xs text-on-surface-variant">
                      {material.type === 'VIDEO'
                        ? t(copy.materialDetail.finishVideo)
                        : material.type === 'AUDIO'
                          ? t(copy.materialDetail.listenAudio)
                          : t(copy.materialDetail.completeContent)}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleComplete}
                  disabled={completePending || !currentEnrollment}
                  className="min-h-12 w-full justify-center gap-2 rounded-lg bg-gradient-to-r from-primary to-primary-container text-on-primary active:scale-95 sm:w-auto"
                >
                  {completePending ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <CheckCircle2 size={18} />
                  )}
                  {t(copy.materialDetail.iHaveLearned)}
                </Button>
                {completeFailed && (
                  <p role="alert" className="m-0 text-sm font-medium text-error sm:basis-full">
                    {t(copy.materialDetail.completeFailed)}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Description Section */}
          {contextText && (
            <section id="material-context" className="scroll-mt-24 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-1 h-6 bg-primary rounded-full" />
                <h2 className="text-xl font-headline font-semibold text-on-surface">
                  {t(copy.materialDetail.materialContext)}
                </h2>
              </div>
              <div className="whitespace-pre-wrap rounded-3xl bg-surface-container-lowest p-5 text-sm leading-7 text-on-surface font-body sm:p-8 sm:text-base">
                {contextText}
              </div>
            </section>
          )}

          {material.media && material.media.length > 0 && (
            <section id="material-attachments" className="scroll-mt-24 space-y-4">
              <h2 className="font-headline text-xl font-bold text-on-surface">
                {t(copy.materialDetail.attachments)}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {[...material.media]
                  .sort((a, b) => a.order - b.order)
                  .map((item: MaterialMedia) => {
                    const AssetIcon =
                      item.type === 'VIDEO'
                        ? Video
                        : item.type === 'AUDIO'
                          ? Music
                          : item.type === 'PDF'
                            ? FileText
                            : ExternalLink;
                    const url = resolveMediaUrl(item.url, apiBaseUrl);
                    return url ? (
                      <a
                        key={item.id}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${t(copy.materialDetail.openAttachment)}: ${item.filename || item.title || t(copy.materialDetail.untitledAsset)}`}
                        className="flex min-h-16 min-w-0 items-center gap-3 rounded-xl bg-surface-container-low p-4 text-on-surface no-underline transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                      >
                        <AssetIcon size={20} className="shrink-0 text-primary" aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                          {item.filename || item.title || t(copy.materialDetail.untitledAsset)}
                        </span>
                        <ExternalLink
                          size={16}
                          className="shrink-0 text-on-surface-variant"
                          aria-hidden="true"
                        />
                      </a>
                    ) : null;
                  })}
              </div>
            </section>
          )}

          {/* Reflection Loop Section */}
          {(showReflectionForm || isCompleted) && (
            <section
              id="material-reflection"
              className="scroll-mt-24 animate-slideInUp space-y-4 pt-4"
            >
              <div className="flex items-center gap-2 text-primary">
                <MessageSquare size={24} />
                <h2 className="text-xl font-headline font-bold">
                  {t(copy.materialDetail.reflectionLoop)}
                </h2>
              </div>

              <div className="space-y-6 rounded-3xl bg-surface-container-lowest p-5 sm:p-8">
                {isCompleted && !showReflectionForm && (
                  <div className="rounded-2xl bg-primary/5 py-4 text-center text-sm text-on-surface-variant font-body italic">
                    {t(copy.materialDetail.reflectionSolidify)}
                  </div>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor="material-reflection-text"
                    className="block text-sm font-bold text-on-surface"
                  >
                    {t(copy.materialDetail.keyTakeaways)}
                  </label>
                  <div className="relative">
                    <textarea
                      id="material-reflection-text"
                      className="min-h-[160px] w-full resize-y rounded-xl bg-surface-container-high p-4 pb-9 text-sm font-body text-on-surface outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      placeholder={t(copy.materialDetail.takeawaysPlaceholder)}
                      value={reflectionText}
                      onChange={e => setReflectionText(e.target.value)}
                    />
                    <div className="absolute bottom-4 right-4 italic text-[10px] text-on-surface-variant opacity-40">
                      {reflectionText.length} {t(copy.materialDetail.characters)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2 text-[10px] text-on-surface-variant uppercase tracking-widest font-bold">
                    <AlertCircle size={14} className="text-secondary" />
                    {t(copy.materialDetail.mentorsOnly)}
                  </div>
                  <Button
                    onClick={handleReflectionSubmit}
                    disabled={!reflectionText.trim() || submittingReflection}
                    className="min-h-11 justify-center gap-2 rounded-lg px-8 active:scale-95"
                  >
                    {submittingReflection ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Send size={18} />
                    )}
                    {t(copy.materialDetail.submitReflection)}
                  </Button>
                </div>
                {reflectionFailed && (
                  <p role="alert" className="m-0 text-sm font-medium text-error">
                    {t(copy.materialDetail.reflectionFailed)}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar Space */}
        <aside className="space-y-6">
          {/* Summary Stats */}
          <div className="space-y-6 rounded-3xl bg-surface-container-low p-5 sm:p-6">
            <div>
              <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-[0.2em] mb-4 font-bold">
                {t(copy.materialDetail.curriculumContext)}
              </p>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-body text-on-surface-variant">
                    {t(copy.materialDetail.estimated)}
                  </span>
                  <span className="text-xs font-bold text-on-surface">
                    {material.duration || 15} {t(copy.materialDetail.minRead)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-body text-on-surface-variant">
                    {t(copy.materialDetail.component)}
                  </span>
                  <span className="text-[10px] font-bold text-secondary bg-secondary/5 px-2 py-0.5 rounded uppercase tracking-tighter">
                    {t(copy.materialDetail.essential)}
                  </span>
                </div>
              </div>
            </div>

            {material.evaluations && material.evaluations.length > 0 && (
              <div
                id="material-assessments"
                className="scroll-mt-24 space-y-4 rounded-2xl bg-secondary-container/40 p-4"
              >
                <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-[0.2em] font-bold">
                  {t(copy.materialDetail.assessments)}
                </p>
                <div className="space-y-2">
                  {material.evaluations.map(ev => (
                    <Link
                      key={ev.id}
                      to={`/evaluations/${ev.id}`}
                      className="group flex min-h-12 items-center justify-between rounded-lg bg-surface-container-lowest p-3 no-underline transition-colors hover:bg-secondary-container focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={18} className="text-secondary" />
                        <span className="text-xs font-bold text-on-secondary-container">
                          {ev.title}
                        </span>
                      </div>
                      <ArrowRight
                        size={16}
                        className="text-secondary opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Bottom Session Next / Prev Navigation */}
      {(sessionHierarchy || legacyFlow) && (
        <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-3xl bg-surface-container-low p-4 pt-5 sm:flex-row">
          {previousMaterial ? (
            <Link
              to={`/materials/${previousMaterial.id}`}
              aria-label={`${t(copy.materialDetail.prevMaterial)}: ${previousMaterial.title}`}
              className="inline-flex w-full min-w-0 items-center gap-3 rounded-xl bg-surface-container-lowest px-4 py-3 text-on-surface no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] sm:max-w-[48%]"
            >
              <ArrowLeft size={16} className="shrink-0" />
              <span className="min-w-0 text-left">
                <span className="block text-[9px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  {t(copy.materialDetail.prevMaterial)}
                  {sessionHierarchy?.previousMaterialSession &&
                    ` · #${sessionHierarchy.previousMaterialSession.sessionNumber}`}
                </span>
                <span className="mt-1 block truncate text-xs font-bold">
                  {previousMaterial.title}
                </span>
              </span>
            </Link>
          ) : (
            <div className="hidden sm:block" />
          )}

          {followingMaterial ? (
            <Link
              to={`/materials/${followingMaterial.id}`}
              aria-label={`${t(copy.materialDetail.nextMaterial)}: ${followingMaterial.title}`}
              className="inline-flex w-full min-w-0 items-center justify-end gap-3 rounded-xl bg-primary px-4 py-3 text-on-primary no-underline shadow-level1 transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] sm:max-w-[48%]"
            >
              <span className="min-w-0 text-right">
                <span className="block text-[9px] font-label font-bold uppercase tracking-wider text-on-primary/75">
                  {t(copy.materialDetail.nextMaterial)}
                  {sessionHierarchy?.followingMaterialSession &&
                    ` · #${sessionHierarchy.followingMaterialSession.sessionNumber}`}
                </span>
                <span className="mt-1 block truncate text-xs font-bold">
                  {followingMaterial.title}
                </span>
              </span>
              <ArrowRight size={16} className="shrink-0" />
            </Link>
          ) : (
            <Link
              to={curriculumReturnUrl}
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-secondary px-6 py-3.5 text-xs font-bold text-on-secondary no-underline shadow-level1 transition-colors hover:bg-secondary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 sm:w-auto"
            >
              <span>{t(copy.materialDetail.backToCurriculum)}</span>
              <ArrowRight size={16} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
